import { useEffect, useRef } from "react";
import { RouterProvider } from "react-router/dom";
import ErrorBoundary from '@common/feedback/errorBoundary';
import LoadingSkeleton from '@common/feedback/LoadingSkeleton';
import { router } from '@/navigation';
import { SocketProvider } from '@/redux/socketContext';
import { StagingGate } from '@/component/auth/staging-gate/StagingGate';
import { requireEnv } from '@/utils/env.js';
import { reloadOnNewVersion } from '@/utils/versionReload';

// Version check runs synchronously before any component renders.
// If there's a version mismatch (post-deploy), reload immediately so the
// router never mounts with stale chunk references. Previously this ran in
// useEffect, which fired AFTER the first render — React Router would start
// loading lazy routes, then the reload would abort navigation mid-flight,
// causing a white screen flash on every first click after a deploy.
// The check itself, and why a first visit only stamps: utils/versionReload.js.
const currentVersion = requireEnv('VITE_VERSION');
if (currentVersion) {
  reloadOnNewVersion(currentVersion, () => window.location.reload());
}

function App() {
  // Version checking via /version.txt (production only)
  // Polls for deploy changes while the tab is open — complements the cookie
  // check which only fires on initial page load with a new index.html
  const versionRef = useRef(null);
  useEffect(() => {
    if (import.meta.env.DEV) {
      return;
    }

    let intervalId = null;
    let isMounted = true;

    const checkVersion = async () => {
      try {
        const res = await fetch("/version.txt");
        if (!res.ok) return;
        const version = (await res.text()).trim();

        if (!isMounted) return;

        if (!versionRef.current) {
          versionRef.current = version;
        } else if (versionRef.current !== version) {
          window.location.reload();
        }
      } catch {
        // Silently handle errors
      }
    };

    checkVersion();
    intervalId = setInterval(checkVersion, 30000);

    return () => {
      isMounted = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  return (
    <ErrorBoundary>
      {/* The staging gate sits outside the socket provider so no
          handshake is attempted before the gate is open; the socket is
          gated too and would only churn connect_error until then. */}
      <StagingGate>
        <SocketProvider>
          <RouterProvider
            router={router}
            fallbackElement={<LoadingSkeleton type="page" />}
            hydrateFallbackElement={null}
          />
        </SocketProvider>
      </StagingGate>
    </ErrorBoundary>
  );
}

export default App;
